import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AppProvider, useAppContext } from './context/AppContext'; // <-- 1. Imported Provider
import authService from './services/authService'; // <-- ADDED: Import authService
import AppLayout from './components/layout/AppLayout';
import Login from './pages/Login';
import Logout from './pages/Logout';
import Welcome from './pages/Welcome';
import Imports from './pages/Imports';
import MasterDataPage from './pages/MasterData/MasterDataPage';
import BudgetGrid from './pages/BudgetGrid'; 
import Settings from './pages/Settings';
import BudgetSetup from './pages/BudgetSetup'; // <-- ADDED: Import the new BudgetSetup page
import VolumeGridPage from './pages/BudgetSetup/VolumeGridPage';
import CustomerMasterPage from './pages/Revenue/CustomerMasterPage';
import DiscountConfig from './pages/Revenue/DiscountConfig';
import Purchase from './pages/Purchase/Purchase';
import StockedPricing from './pages/Purchase/StockedPricing';
import MonthlyPricing from './pages/Purchase/MonthlyPricing';
import NonStockedPricing from './pages/Purchase/NonStockedPricing';
import PurchaseBudgetSetup from './pages/Purchase/PurchaseBudgetSetup';
import ProcurementCosting from './pages/Purchase/ProcurementCosting';
import RequestedStockCodes from './pages/Purchase/RequestedStockCodes';
import Manufacturing from './pages/Manufacturing/Manufacturing';
import BillOfMaterials from './pages/Manufacturing/BillOfMaterials';
import WorkCentreCosting from './pages/Manufacturing/WorkCentreCosting';
import OvertimeRules from './pages/Manufacturing/OvertimeRules';
import ManufacturingCosting from './pages/Manufacturing/ManufacturingCosting';
import MfgFixedCosts from './pages/Manufacturing/FixedCosts';
import MfgVariableLabour from './pages/Manufacturing/VariableLabour';
import MaintenanceBudget from './pages/Manufacturing/MaintenanceBudget';
import Distribution from './pages/Distribution/Distribution';
import FixedCosts from './pages/Distribution/Warehousing/FixedCosts';
import VariableLabour from './pages/Distribution/Warehousing/VariableLabour';
import OperationalDrivers from './pages/Distribution/Warehousing/OperationalDrivers';
import VariableRunningCosts from './pages/Distribution/Warehousing/VariableRunningCosts';
import VariableExpenseSetup from './pages/Distribution/BudgetSetup/VariableExpenseSetup';
import Contractors from './pages/Distribution/BudgetSetup/Contractors';
import TransferPosting from './pages/Distribution/BudgetSetup/TransferPosting';
import PriceIndices from './pages/Distribution/BudgetSetup/PriceIndices';
import Finance from './pages/Finance/Finance';
import SubmissionStatus from './pages/Finance/SubmissionStatus';
import ProfitAndLoss from './pages/Finance/ProfitAndLoss';
import Journals from './pages/Finance/Journals';
import CashFlow from './pages/Finance/CashFlow';
import BalanceSheet from './pages/Finance/BalanceSheet';
import WorkingCapital from './pages/Finance/WorkingCapital';
import Depreciation from './pages/Finance/Depreciation';
import InterDepot from './pages/Distribution/InterDepot/InterDepot';
// Secondary distribution on the zone + carrier model: one setup page feeding
// two budget pages. Replaced the RouteToMarket and StoreReplenishment mockups,
// which budgeted per customer by hand — that survives neither real customer
// counts nor a shared multi-drop truck.
import DeliveryZones from './pages/Distribution/Secondary/DeliveryZones';
import SecondaryPosting from './pages/Distribution/BudgetSetup/SecondaryPosting';
import SecondaryLanes from './pages/Distribution/Secondary/SecondaryLanes';
import CustomerDelivery from './pages/Distribution/Secondary/CustomerDelivery';
import StoreReplenishmentZones from './pages/Distribution/Secondary/StoreReplenishment';
import NetworkPlanning from './pages/Planning/NetworkPlanning';
// NOTE: WarehouseCosts.jsx and FleetRoutes.jsx are superseded by the Warehousing
// pages above and are no longer routed. Files kept on disk — FleetRoutes will
// seed the future "Route to Market" section.
import Capital from './pages/Capital/Capital';
import CapitalProjects from './pages/Capital/CapitalProjects';
import AssetRegister from './pages/Capital/AssetRegister';
import Revenue from './pages/Revenue/Revenue';
import SellingPrices from './pages/Revenue/SellingPrices';
import Discounts from './pages/Revenue/Discounts';
import PriceImplosion from './pages/Revenue/PriceImplosion';
import RevenuePosting from './pages/Revenue/RevenuePosting';
import CogsPosting from './pages/Manufacturing/CogsPosting';
import People from './pages/People/People';
import Structures from './pages/People/Structures';
import JobTitles from './pages/People/JobTitles';
import Grades from './pages/People/Grades';
import GlMapping from './pages/People/GlMapping';
import Reports from './pages/Reports/Reports';
import Insights from './pages/Insights/Insights';
import ReportSection from './pages/Reports/ReportSection';
import SkuProfitability from './pages/Reports/SkuProfitability';
import GenericReport from './pages/Reports/GenericReport';
import SubmitBudgetButton from './components/SubmitBudgetButton';

const dateNow = new Date();
const year = ('' + dateNow.getFullYear()).substr(2);

// const Settings = () => <h2>System Settings</h2>;

// Route guard for SENSITIVE modules (e.g. People / payroll). Unlike the other
// modules, these are never unlocked by the admin blanket — the currently
// selected role must hold the module explicitly (Settings > Role Data Access),
// so navigating straight to the URL without the grant bounces to Welcome.
const RequireModule = ({ module, children }) => {
  const { roleModules } = useAppContext();
  return roleModules.includes(module)
    ? children
    : <Navigate to="/welcome" replace />;
};

const Opex = () => (
  <div className="p-6 h-full flex flex-col overflow-hidden bg-slate-100 dark:bg-slate-900">
    <div className="shrink-0 mb-6 flex items-start justify-between gap-4">
      <div>
        <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Opex Budget Capture</h2>
        <p className="text-slate-500 dark:text-slate-400">Capture and adjust your budget. Changes save automatically.</p>
      </div>
      <SubmitBudgetButton module="MOD_1_OPEX" />
    </div>
    <div className="flex-1 min-h-0">
      <BudgetGrid />
    </div>
  </div>
);

function App() {
  // FIX 1: Initialize state by checking local storage for a valid token.
  // Using a callback () => ensures this only runs once on initial load.
  const [isAuthenticated, setIsAuthenticated] = useState(() => authService.isAuthenticated());

  // If they are NOT logged in, return early and ONLY show the unauthenticated screens
  if (!isAuthenticated) {
    return (
      <Router>
        <Routes>
          <Route path="/login" element={<Login onLoginSuccess={() => setIsAuthenticated(true)} />} />
                    
          {/* We keep logout here just in case a stale session redirects here */}
          <Route path="/logout" element={<Logout onLogout={() => setIsAuthenticated(false)} />} />
          
          {/* Catch-all route to redirect unknown URLs back to safety */}
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Router>
    );
  }

  // If they ARE logged in, render the main application
  return (
    // WRAP THE ENTIRE APP IN THE PROVIDER
    // This allows TopHeader and BudgetGrid to share the same state seamlessly.
    <AppProvider>
      <Router>
        {/* FIX 2: Place Logout OUTSIDE the AppLayout so it renders full screen, 
            but INSIDE the authenticated router so logged-in users can reach it. */}
        <Routes>
          <Route path="/logout" element={<Logout onLogout={() => setIsAuthenticated(false)} />} />
          
          {/* All other routes are wrapped in the layout */}
          <Route path="*" element={
            <AppLayout>
              <Routes>
                {/* Land on the Welcome dashboard after login, not a feature page. */}
                <Route path="/" element={<Navigate to="/welcome" replace />} />

                <Route path="/welcome" element={<Welcome />} />
                {/* Reporting (MOD_10_REPORT) — one module, many sections, and
                    the section is the unit of access. Full pages, not modals:
                    a report list is somewhere you arrive and link people to. */}
                {/* AI Insights (MOD_11_INSIGHT). The prompt IS the page - see the
                    header comment in Insights.jsx on why this is not a modal. */}
                <Route path="/insights" element={<Insights />} />
                <Route path="/reports" element={<Reports />} />
                <Route path="/reports/:sectionCode" element={<ReportSection />} />
                {/* SKU Profitability keeps its bespoke page; it is declared
                    BEFORE the catch-all so the literal path wins. Every other
                    report renders from its dataset via GenericReport. */}
                <Route path="/reports/:sectionCode/SKU_PROFIT" element={<SkuProfitability />} />
                <Route path="/reports/:sectionCode/:reportCode" element={<GenericReport />} />
                <Route path="/opex" element={<Opex />} />
                <Route path="/imports" element={<Imports />} />
                {/* One page serves every md_* table; :type keys into MASTER_SPECS. */}
                <Route path="/master-data/:type" element={<MasterDataPage />} />
                <Route path="/budget-setup" element={<BudgetSetup />} /> {/* <-- ADDED: Budget Setup Route */}
                <Route path="/purchase" element={<Purchase />} />
                <Route path="/purchase/stocked" element={<StockedPricing />} />
                <Route path="/purchase/monthly-pricing" element={<MonthlyPricing />} />
                <Route path="/purchase/non-stocked" element={<NonStockedPricing />} />
                <Route path="/purchase/budget-setup" element={<PurchaseBudgetSetup />} />
                <Route path="/purchase/costing" element={<ProcurementCosting />} />
                <Route path="/purchase/requested-stock-codes" element={<RequestedStockCodes />} />
                <Route path="/manufacturing" element={<Manufacturing />} />
                <Route path="/manufacturing/bom" element={<BillOfMaterials />} />
                <Route path="/manufacturing/work-centers-costing" element={<WorkCentreCosting />} />
                <Route path="/manufacturing/overtime" element={<OvertimeRules />} />
                <Route path="/manufacturing/costing" element={<ManufacturingCosting />} />
                <Route path="/manufacturing/cogs-posting" element={<CogsPosting />} />
                <Route path="/manufacturing/production-volumes" element={<VolumeGridPage kind="production" />} />
                <Route path="/manufacturing/fixed-costs" element={<MfgFixedCosts />} />
                <Route path="/manufacturing/variable-labour" element={<MfgVariableLabour />} />
                <Route path="/manufacturing/maintenance" element={<MaintenanceBudget />} />
                <Route path="/distribution" element={<Distribution />} />
                <Route path="/distribution/fixed-costs" element={<FixedCosts />} />
                <Route path="/distribution/variable-labour" element={<VariableLabour />} />
                <Route path="/distribution/operational-drivers" element={<OperationalDrivers />} />
                <Route path="/distribution/variable-running" element={<VariableRunningCosts />} />
                <Route path="/distribution/budget-setup/variable-expenses" element={<VariableExpenseSetup />} />
                <Route path="/distribution/budget-setup/contractors" element={<Contractors />} />
                <Route path="/distribution/budget-setup/transfer-posting" element={<TransferPosting />} />
                <Route path="/distribution/budget-setup/price-indices" element={<PriceIndices />} />
                {/* Finance (MOD_7_FIN) — cross-site consolidation and control. */}
                <Route path="/finance" element={<Finance />} />
                <Route path="/finance/submissions" element={<SubmissionStatus />} />
                <Route path="/finance/profit-and-loss" element={<ProfitAndLoss />} />
                <Route path="/finance/journals" element={<Journals />} />
                <Route path="/finance/cash-flow" element={<CashFlow />} />
                <Route path="/finance/balance-sheet" element={<BalanceSheet />} />
                <Route path="/finance/working-capital" element={<WorkingCapital />} />
                <Route path="/finance/depreciation" element={<Depreciation />} />
                <Route path="/distribution/inter-depot" element={<InterDepot />} />
                <Route path="/distribution/delivery-zones" element={<DeliveryZones />} />
                <Route path="/distribution/budget-setup/secondary-posting" element={<SecondaryPosting />} />
                <Route path="/distribution/secondary-lanes" element={<SecondaryLanes />} />
                <Route path="/distribution/customer-delivery" element={<CustomerDelivery />} />
                <Route path="/distribution/store-delivery" element={<StoreReplenishmentZones />} />
                {/* Network planning — cross-site by construction, so it sits at
                    the top level rather than under a site-scoped module. Lane
                    volumes open as a popup ON the map, not as their own route:
                    the planner is working the network, not visiting pages. */}
                <Route path="/planning" element={<NetworkPlanning />} />
                <Route path="/capital" element={<Capital />} />
                <Route path="/capital/projects" element={<CapitalProjects />} />
                <Route path="/capital/assets" element={<AssetRegister />} />
                {/* Depreciation moved to Finance — it is a financial policy decision,
                    not a maintenance one. Redirect rather than 404: the old path is
                    linked from elsewhere and a dead link is worse than a move. */}
                <Route path="/capital/depreciation" element={<Navigate to="/finance/depreciation" replace />} />
                <Route path="/revenue" element={<Revenue />} />
                <Route path="/revenue/sales-volumes" element={<VolumeGridPage kind="sales" />} />
                <Route path="/revenue/customer-groups" element={<CustomerMasterPage kind="groups" />} />
                <Route path="/revenue/customers" element={<CustomerMasterPage kind="customers" />} />
                <Route path="/revenue/prices" element={<SellingPrices />} />
                <Route path="/revenue/discounts" element={<Discounts />} />
                <Route path="/revenue/discount-config" element={<DiscountConfig />} />
                <Route path="/revenue/implosion" element={<PriceImplosion />} />
                <Route path="/revenue/budget-setup/posting" element={<RevenuePosting />} />
                <Route path="/people" element={<RequireModule module="MOD_8_PEOPLE"><People /></RequireModule>} />
                <Route path="/people/structures" element={<RequireModule module="MOD_8_PEOPLE"><Structures /></RequireModule>} />
                <Route path="/people/person" element={<Navigate to="/people/structures" replace />} />
                <Route path="/people/job-titles" element={<RequireModule module="MOD_8_PEOPLE"><JobTitles /></RequireModule>} />
                <Route path="/people/grades" element={<RequireModule module="MOD_8_PEOPLE"><Grades /></RequireModule>} />
                <Route path="/people/gl-mapping" element={<RequireModule module="MOD_8_PEOPLE"><GlMapping /></RequireModule>} />
                <Route path="/settings" element={<Settings />} />
                
                {/* Catch-all route to redirect unknown URLs back to safety */}
                <Route path="*" element={<Navigate to="/welcome" replace />} />
              </Routes>
            </AppLayout>
          } />
        </Routes>
      </Router>
    </AppProvider>
  );
}

export default App;
